import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { getBankAccounts, addBankAccount, updateBankAccount } from '../../api/api';
import PageTitle from '../layouts/PageTitle';
import Loading from '../components/Loading';
import swal from 'sweetalert2';

const BankAccounts = ({ auth }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        accountHolderName: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        walletAddress: '',
        walletName: '',
        networkType: '',
        isPrimary: false
    });
    const [error, setError] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [editFormData, setEditFormData] = useState({
        accountHolderName: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        isPrimary: false,
        status: 'active',
        walletAddress: '',
        walletName: '',
        networkType: '',
        hasCryptoWallet: false
    });

    // Update network type options to match backend enum
    const NETWORK_TYPES = [
        { value: '', label: 'Select Network' },
        { value: 'TRC-20', label: 'TRC-20' },
        { value: 'ERC-20', label: 'ERC-20' },
        { value: 'BEP-20', label: 'BEP-20' },
        { value: 'Other', label: 'Other' }
    ];

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.token) {
            navigate('/login', { replace: true });
            return;
        }
        
        fetchBankAccounts();
    }, [auth, navigate]);

    const fetchBankAccounts = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getBankAccounts();
            
            if (response.success) {
                setAccounts(response.data || []);
            } else {
                throw new Error(response.message || 'Failed to fetch bank accounts');
            }
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
            setError(error.message);
            swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message
            });
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const validateWalletAddress = (address, networkType) => {
        if (!address || !networkType) return true; // Skip if either is empty

        // TRC-20 addresses start with T and are 34 characters long
        if (networkType === 'TRC-20') {
            return /^T[a-zA-Z0-9]{33}$/.test(address);
        }
        
        // ERC-20 and BEP-20 addresses are 42 characters long and start with 0x
        if (networkType === 'ERC-20' || networkType === 'BEP-20') {
            return /^0x[a-fA-F0-9]{40}$/.test(address);
        }

        // For 'Other' network type, just ensure it's not empty and at least 10 chars
        if (networkType === 'Other') {
            return address.length >= 10;
        }

        return true;
    };

    const validateForm = () => {
        const requiredFields = ['accountHolderName', 'bankName', 'accountNumber', 'ifscCode'];
        const emptyFields = requiredFields.filter(field => !formData[field]);
        
        if (emptyFields.length > 0) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: `Please fill in all required fields: ${emptyFields.join(', ')}`
            });
            return false;
        }

        if (formData.accountNumber.length < 8) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Account number must be at least 8 characters long'
            });
            return false;
        }

        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode)) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please enter a valid IFSC code'
            });
            return false;
        }

        // Validate wallet address if provided
        if (formData.walletAddress || formData.networkType) {
            if (!formData.networkType) {
                swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: 'Please select a network type for the wallet'
                });
                return false;
            }

            if (!formData.walletAddress) {
                swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: 'Please enter a wallet address'
                });
                return false;
            }

            if (!validateWalletAddress(formData.walletAddress, formData.networkType)) {
                swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: `Invalid wallet address format for ${formData.networkType} network`
                });
                return false;
            }
        }

        return true;
    };

    // Add helper text for wallet address format
    const getWalletAddressHelperText = () => {
        switch (formData.networkType) {
            case 'TRC-20':
                return 'TRC-20 addresses start with T and are 34 characters long';
            case 'ERC-20':
            case 'BEP-20':
                return 'Addresses start with 0x and are 42 characters long';
            case 'Other':
                return 'Enter a valid wallet address (minimum 10 characters)';
            default:
                return 'Select a network type first';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            setError(null);

            const response = await addBankAccount(formData);

            if (response.success) {
                swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Bank account added successfully'
                });
                setShowAddModal(false);
                setFormData({
                    accountHolderName: '',
                    bankName: '',
                    accountNumber: '',
                    ifscCode: '',
                    walletAddress: '',
                    walletName: '',
                    networkType: '',
                    isPrimary: false
                });
                fetchBankAccounts();
            }
        } catch (error) {
            console.error('Error adding bank account:', error);
            setError(error.message);
            swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSetPrimary = async (accountId) => {
        try {
            setLoading(true);
            const response = await updateBankAccount(accountId, {
                isPrimary: true
            });

            if (response.success) {
                swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Primary bank account updated successfully'
                });
                fetchBankAccounts();
            }
        } catch (error) {
            console.error('Error updating bank account:', error);
            swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (account) => {
        setEditingAccount(account);
        setEditFormData({
            accountHolderName: account.accountHolderName,
            bankName: account.bankName,
            accountNumber: account.accountNumber,
            ifscCode: account.ifscCode,
            isPrimary: account.isPrimary,
            status: account.status,
            walletAddress: account.cryptoDetails?.walletAddress || '',
            walletName: account.cryptoDetails?.walletName || '',
            networkType: account.cryptoDetails?.networkType || '',
            hasCryptoWallet: account.hasCryptoWallet
        });
        setShowEditModal(true);
    };

    const handleEditInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const validateEditForm = () => {
        const requiredFields = ['accountHolderName', 'bankName', 'accountNumber', 'ifscCode'];
        const emptyFields = requiredFields.filter(field => !editFormData[field]);
        
        if (emptyFields.length > 0) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: `Please fill in all required fields: ${emptyFields.join(', ')}`
            });
            return false;
        }

        if (editFormData.accountNumber.length < 8) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Account number must be at least 8 characters long'
            });
            return false;
        }

        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(editFormData.ifscCode)) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please enter a valid IFSC code'
            });
            return false;
        }

        // Validate wallet address if provided
        if (editFormData.walletAddress || editFormData.networkType) {
            if (!editFormData.networkType) {
                swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: 'Please select a network type for the wallet'
                });
                return false;
            }

            if (!editFormData.walletAddress) {
                swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: 'Please enter a wallet address'
                });
                return false;
            }

            if (!validateWalletAddress(editFormData.walletAddress, editFormData.networkType)) {
                swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: `Invalid wallet address format for ${editFormData.networkType} network`
                });
                return false;
            }
        }

        return true;
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!validateEditForm()) return;

        try {
            setSubmitting(true);
            setError(null);

            // Prepare update data with same structure as add
            const updateData = {
                accountHolderName: editFormData.accountHolderName,
                bankName: editFormData.bankName,
                accountNumber: editFormData.accountNumber,
                ifscCode: editFormData.ifscCode,
                walletAddress: editFormData.walletAddress || '',
                walletName: editFormData.walletName || '',
                networkType: editFormData.networkType || '',
                isPrimary: editFormData.isPrimary,
                status: editFormData.status
            };

            const response = await updateBankAccount(editingAccount._id, updateData);

            if (response.success) {
                swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Bank account updated successfully'
                });
                setShowEditModal(false);
                setEditingAccount(null);
                setEditFormData({
                    accountHolderName: '',
                    bankName: '',
                    accountNumber: '',
                    ifscCode: '',
                    isPrimary: false,
                    status: 'active',
                    walletAddress: '',
                    walletName: '',
                    networkType: '',
                    hasCryptoWallet: false
                });
                fetchBankAccounts();
            }
        } catch (error) {
            console.error('Error updating bank account:', error);
            swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || error.message || 'Failed to update bank account'
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Loading />;

    if (error) {
        return (
            <>
                <PageTitle activeMenu="Bank Accounts" motherMenu="Account" />
                <Card>
                    <Card.Body>
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    </Card.Body>
                </Card>
            </>
        );
    }

    return (
        <>
            <PageTitle activeMenu="Bank Accounts" motherMenu="Account" />

            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <Card.Title>Bank Accounts</Card.Title>
                    <Button variant="primary" onClick={() => setShowAddModal(true)}>
                        Add New Account
                    </Button>
                </Card.Header>
                <Card.Body>
                    {!accounts || accounts.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="mb-0">No bank accounts added yet</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th>Account Name</th>
                                        <th>Bank Name</th>
                                        <th>Account Number</th>
                                        <th>IFSC Code</th>
                                        <th>Crypto Wallet</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accounts.map((account) => (
                                        <tr key={account._id}>
                                            <td>{account.accountHolderName}</td>
                                            <td>{account.bankName}</td>
                                            <td>{account.accountNumber}</td>
                                            <td>{account.ifscCode}</td>
                                            <td>
                                                {account.hasCryptoWallet ? (
                                                    <div>
                                                        <Badge bg="info" className="me-2">
                                                            {account.cryptoDetails.networkType}
                                                        </Badge>
                                                        <small title={account.cryptoDetails.walletAddress}>
                                                            {account.cryptoDetails.walletAddress.substring(0, 8)}...
                                                        </small>
                                                        {account.cryptoDetails.walletName && (
                                                            <div className="small text-muted mt-1">
                                                                {account.cryptoDetails.walletName}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <Badge bg="secondary">No Wallet</Badge>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column gap-1">
                                                    <Badge bg={account.status === 'active' ? 'success' : 'danger'}>
                                                        {account.status.toUpperCase()}
                                                    </Badge>
                                                    {account.isPrimary && (
                                                        <Badge bg="primary">Primary</Badge>
                                                    )}
                                                    <Badge bg={account.isVerified ? 'success' : 'warning'}>
                                                        {account.isVerified ? 'Verified' : 'Unverified'}
                                                    </Badge>
                                                </div>
                                            </td>
                                            <td>
                                                <Button 
                                                    variant="light" 
                                                    size="sm"
                                                    onClick={() => handleEdit(account)}
                                                    className="me-2"
                                                >
                                                    Edit
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Bank Account</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Account Holder Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="accountHolderName"
                                value={formData.accountHolderName}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Bank Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="bankName"
                                value={formData.bankName}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Account Number</Form.Label>
                            <Form.Control
                                type="text"
                                name="accountNumber"
                                value={formData.accountNumber}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>IFSC Code</Form.Label>
                            <Form.Control
                                type="text"
                                name="ifscCode"
                                value={formData.ifscCode}
                                onChange={handleInputChange}
                                required
                            />
                            <Form.Text className="text-muted">
                                Format: ABCD0123456 (4 letters followed by 0 and 6 alphanumeric characters)
                            </Form.Text>
                        </Form.Group>

                        <hr className="my-4" />

                        {/* New wallet fields */}
                        <h6 className="mb-3">Crypto Wallet Details (Optional)</h6>

                        <Form.Group className="mb-3">
                            <Form.Label>Wallet Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="walletName"
                                value={formData.walletName}
                                onChange={handleInputChange}
                                placeholder="e.g., My TRX Wallet"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Network Type</Form.Label>
                            <Form.Select
                                name="networkType"
                                value={formData.networkType}
                                onChange={handleInputChange}
                            >
                                {NETWORK_TYPES.map(network => (
                                    <option key={network.value} value={network.value}>
                                        {network.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Wallet Address</Form.Label>
                            <Form.Control
                                type="text"
                                name="walletAddress"
                                value={formData.walletAddress}
                                onChange={handleInputChange}
                                placeholder="Enter your wallet address"
                                isInvalid={formData.walletAddress && !validateWalletAddress(formData.walletAddress, formData.networkType)}
                            />
                            <Form.Text className="text-muted">
                                {getWalletAddressHelperText()}
                            </Form.Text>
                            <Form.Control.Feedback type="invalid">
                                Invalid wallet address format
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                name="isPrimary"
                                label="Set as primary account"
                                checked={formData.isPrimary}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <div className="text-end">
                            <Button variant="secondary" onClick={() => setShowAddModal(false)} className="me-2">
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? 'Adding...' : 'Add Account'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Bank Account</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleEditSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Account Holder Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="accountHolderName"
                                value={editFormData.accountHolderName}
                                onChange={handleEditInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Bank Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="bankName"
                                value={editFormData.bankName}
                                onChange={handleEditInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Account Number</Form.Label>
                            <Form.Control
                                type="text"
                                name="accountNumber"
                                value={editFormData.accountNumber}
                                onChange={handleEditInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>IFSC Code</Form.Label>
                            <Form.Control
                                type="text"
                                name="ifscCode"
                                value={editFormData.ifscCode}
                                onChange={handleEditInputChange}
                                required
                            />
                            <Form.Text className="text-muted">
                                Format: ABCD0123456 (4 letters followed by 0 and 6 alphanumeric characters)
                            </Form.Text>
                        </Form.Group>

                        <hr className="my-4" />
                        <h6 className="mb-3">Crypto Wallet Details</h6>

                        <Form.Group className="mb-3">
                            <Form.Label>Wallet Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="walletName"
                                value={editFormData.walletName}
                                onChange={handleEditInputChange}
                                placeholder="e.g., My TRX Wallet"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Network Type</Form.Label>
                            <Form.Select
                                name="networkType"
                                value={editFormData.networkType}
                                onChange={handleEditInputChange}
                            >
                                {NETWORK_TYPES.map(network => (
                                    <option key={network.value} value={network.value}>
                                        {network.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Wallet Address</Form.Label>
                            <Form.Control
                                type="text"
                                name="walletAddress"
                                value={editFormData.walletAddress}
                                onChange={handleEditInputChange}
                                placeholder="Enter your wallet address"
                                isInvalid={editFormData.walletAddress && !validateWalletAddress(editFormData.walletAddress, editFormData.networkType)}
                            />
                            <Form.Text className="text-muted">
                                {getWalletAddressHelperText()}
                            </Form.Text>
                            <Form.Control.Feedback type="invalid">
                                Invalid wallet address format
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                name="isPrimary"
                                label="Set as primary account"
                                checked={editFormData.isPrimary}
                                onChange={handleEditInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                name="status"
                                value={editFormData.status}
                                onChange={handleEditInputChange}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </Form.Select>
                        </Form.Group>

                        <div className="text-end">
                            <Button variant="secondary" onClick={() => setShowEditModal(false)} className="me-2">
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? 'Updating...' : 'Update Account'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps)(BankAccounts); 